import React, { useMemo } from 'react';

import { ImageIcon, ZoomInIcon, MoreHorizontalIcon } from '@vlabs/icons';
import { ButtonWithPermissions } from '@vlabs/monokit/shared/ui/button-with-permissions';
import { ButtonScrimGroup, Control, useModal } from '@vlabs/uikit';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { viewerCan } from '@vlabs/pages/auth/selectors';

import { FullFrame } from '@vlabs/shared/components/images/FullFrame';
import { Track } from '@vlabs/shared/components/images/Track';
import { permissions } from '@vlabs/shared/config';
import { useImageCheck } from '@vlabs/shared/hooks';

function useTrackModal(props) {
  const modal = useModal();
  modal.wrap(Track);
  return {
    Component: modal.wrap(<Track {...props} />),
    isOpen: modal.isOpen,
    open: modal.open,
    close: modal.close,
  };
}

function useFullFrameModal(props) {
  const modal = useModal();

  return {
    Component: modal.wrap(<FullFrame {...props} />),
    isOpen: modal.isOpen,
    open: modal.open,
    close: modal.close,
  };
}

function EventActionsComponent({
  sampleURL = undefined,
  fullFrameURL = undefined,
  track = undefined,
  rect = undefined,
  extraActions = undefined,
  can,
  type = 'face',
}) {
  const { t } = useTranslation();
  const samplePermissionByType = useMemo(() => {
    return type === 'face' ? [permissions.faceSample.view] : [permissions.bodySample.view];
  }, [type]);

  const urls = useMemo(() => ({ sample: sampleURL, fullFrame: fullFrameURL }), [sampleURL, fullFrameURL]);
  const error = useImageCheck(urls);
  const trackModal = useTrackModal({ detections: track });
  const imageOriginModal = useFullFrameModal({ fullFrameURL, rect });

  const isSampleDisabled = !sampleURL || error.sample || !can([permissions.faceSample.view]);
  const isViewTrackDisabled = !track || track.length <= 1 || error.fullFrame;
  const isFullFrameDisabled = !fullFrameURL || error.fullFrame;

  return (
    <React.Fragment>
      {imageOriginModal.Component}
      {trackModal.Component}

      <ButtonScrimGroup
        childrenAs={ButtonWithPermissions}
      >
        <Control.IconButton
          as={isSampleDisabled ? 'div' : 'a'}
          disabled={isSampleDisabled}
          href={sampleURL}
          icon={<ZoomInIcon />}
          permissions={{ rules: samplePermissionByType }}
          round
          title={t('посмотреть в полном размере')}
          variant="dimmed"
        />

        <Control.IconButton
          data-testid="eventActions.viewTrackButton"
          disabled={isViewTrackDisabled}
          icon={<MoreHorizontalIcon />}
          onClick={trackModal.open}
          permissions={{ rules: samplePermissionByType }}
          round
          title={t('просмотр трека')}
          variant="dimmed"
        />

        <Control.IconButton
          data-testid="eventActions.fullFrameButton"
          disabled={isFullFrameDisabled}
          icon={<ImageIcon />}
          onClick={imageOriginModal.open}
          permissions={{ rules: [permissions.image.view] }}
          round
          title={t('полный кадр')}
          variant="dimmed"
        />
        {extraActions}
      </ButtonScrimGroup>
    </React.Fragment>
  );
}

EventActionsComponent.propTypes = {
  can: PropTypes.func.isRequired,
  type: PropTypes.string,
  extraActions: PropTypes.node,
  sampleURL: PropTypes.string,
  track: PropTypes.arrayOf(PropTypes.shape({
    uiSampleURL: PropTypes.string,
  })),
  fullFrameURL: PropTypes.string,
  rect: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
};

export const EventActions = connect((state) => ({
  can: viewerCan(state),
}))(EventActionsComponent);

export function EventFaceActions({
  sampleURL,
  fullFrameURL,
  track,
  rect,
  extraActions,
}) {
  return (
    <EventActions
      extraActions={extraActions}
      fullFrameURL={fullFrameURL}
      rect={rect}
      sampleURL={sampleURL}
      track={track}
      type="face"
    />
  );
}

export function EventBobyActions({
  sampleURL,
  fullFrameURL,
  track,
  rect,
  extraActions,
}) {
  return (
    <EventActions
      extraActions={extraActions}
      fullFrameURL={fullFrameURL}
      rect={rect}
      sampleURL={sampleURL}
      track={track}
      type="body"
    />
  );
}
